import { useRecoilCallback } from 'recoil';
import * as K from 'common/constants';
import { filterUrlParams, getUrlParamSet } from 'common/util/location';
import { Settings, UrlParams } from 'common/types';
import { draftSearchParamState, routeState } from 'components/common/Managers/RouteStateManager.state';
import { updateUrl } from 'components/common/Managers/RouteStateManager.utils';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import browserHistory from 'service/customHistory';

// Routes that are related to events
const EVENTS_ROUTES = [
  K.ROUTE.DISTRIBUTION,
  K.ROUTE.EVENTS,
  K.ROUTE.GRAPH,
  K.ROUTE.TRACE,
  K.ROUTE.POWER_QUERY,
];

/**
 * This hook exposes a simple API for components to use when they want to update URL parameters.
 * (Setting a parameter to empty string will remove).
 *
 * -------------------------------------
 * Example usage:
 * ```
 *  const setSearchParams = useSetSearchParams();
 *  ...
 *
 *  const onSelect = (item: string): void => {
 *    setSearchParams({
 *      breakdownFacet: item,
 *      showGraphs:     '',
 *      hideGraphs:     '',
 *    } as UrlParams, true);
 *  };
 * ```
 * -------------------------------------
 *
 * @returns    A recoil callback that handles taking an input of search parameters (eventParams, viewParams, disposableParams),
 *             makes any necessary updates to history, or may only set draftSearchParamState if not in autoUpdate mode. It also
 *             handles setting the 'updateFlag' for scenarios where eventParams may not have changed, but we want to force a new search anyway.
 */
export const useSetSearchParams = () =>
  useRecoilCallback(({ snapshot, set }) => (parameters: Partial<UrlParams>, force: boolean = false) => {
    const { eventParamList, viewParamList, disposableParamList } = getUrlParamSet(browserHistory.location);
    const route = snapshot.getLoadable(routeState).contents as K.ROUTE;
    const settings = snapshot.getLoadable(settingsState).contents as Settings;
    const isEventsRoute = EVENTS_ROUTES.includes(route);
    const autoUpdateSearch = !isEventsRoute || settings[K.SETTING.AUTO_UPDATE_SEARCH];
    const draftParams = snapshot.getLoadable(draftSearchParamState).contents as UrlParams;
    const filteredParams = filterUrlParams(
      { ...draftParams, ...parameters },
      eventParamList.concat(viewParamList).concat(disposableParamList),
    );

    if (autoUpdateSearch || force) {
      const isOnlyViewParams = Object.keys(parameters).every((key) => viewParamList.includes(key as K.URL_PARAM));
      updateUrl(filteredParams, isOnlyViewParams, browserHistory, { forceUpdate: force });
    } else {
      set(draftSearchParamState, { ...draftParams, ...parameters });
    }
  });
